import { Alert, Button, Checkbox } from '@components'
import { NocfoPlanSource } from '@constants'
import { useBusinessContext } from '@context'
import { enableEinvoicing } from '@query'
import {
  stripeBillingQueryKeys,
  usePaymentMethodSetup,
  usePaymentMethodStatus
} from '@root/query/hooks'
import { BillingSetupModal } from '@root/containers/Billing/BillingSetupPanel'
import React, { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { FaCheck } from 'react-icons/fa'

export const ActivateEInvoicing: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const {
    slug,
    data: { subscription_source: subscriptionSource }
  } = useBusinessContext()
  const [tosAccepted, setTosAccepted] = useState(false)
  const [showError, setShowError] = useState(false)
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [billingModalInitialCard, setBillingModalInitialCard] = useState<string | null>(null)
  const isManualPlan = subscriptionSource === NocfoPlanSource.MANUAL
  const isAgencyCustomer = subscriptionSource === NocfoPlanSource.AGENCY
  const isNonDirectBillingCustomer = isManualPlan || isAgencyCustomer
  const { data: paymentMethodStatus, isLoading: isLoadingPaymentMethod } = usePaymentMethodStatus({
    slug,
    enabled: !isNonDirectBillingCustomer
  })
  const hasDefaultPaymentMethod = paymentMethodStatus?.has_payment_method ?? false
  const hasManualInvoicing = paymentMethodStatus?.has_manual_invoicing ?? false
  const canActivate = isNonDirectBillingCustomer || hasDefaultPaymentMethod || hasManualInvoicing
  const { startPaymentMethodSetup, isStartingPaymentMethodSetup } = usePaymentMethodSetup({
    slug
  })
  const { mutate: enableEInvoicingMutation, isPending: isLoading } = useMutation({
    mutationFn: () => enableEinvoicing({ slug }),
    ...{
      onSuccess: async () => {
        setShowError(false)
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['businesses'] }),
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: stripeBillingQueryKeys.all(slug) })
        ])
      },
      onError: error => {
        const status = (error as any)?.status
        if (status === 400 && !isAgencyCustomer) {
          openBillingModal(null)
          return
        }
        setShowError(true)
      }
    }
  })

  const openBillingModal = (initialCardId: string | null) => {
    setBillingModalInitialCard(initialCardId)
    setBillingModalOpen(true)
  }

  const handleActivate = () => {
    if (canActivate) {
      enableEInvoicingMutation()
      return
    }
    openBillingModal(null)
  }

  const handleManualSubmitSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['businesses'] }),
      queryClient.invalidateQueries({ queryKey: [slug] }),
      queryClient.invalidateQueries({ queryKey: stripeBillingQueryKeys.all(slug) })
    ])
    setBillingModalOpen(false)
  }

  return (
    <StyledContainer>
      <Alert
        isVisible={showError}
        type="error"
        title={t('settings.einvoicing.errorTitle')}
        description={
          <>
            <StyledErrorText>
              <Trans i18nKey="settings.einvoicing.errorDescription">
                <a href={t('link.einvoicing')} target="_blank" rel="noreferrer" />
              </Trans>
            </StyledErrorText>
            <StyledErrorText>{t('settings.einvoicing.errorApixIntro')}</StyledErrorText>
            <StyledErrorList>
              <li>{t('settings.einvoicing.errorApixListExistingServices')}</li>
              <li>{t('settings.einvoicing.errorApixListLaskumappi')}</li>
              <li>{t('settings.einvoicing.errorApixListOtherSoftware')}</li>
              <li>
                <Trans i18nKey="settings.einvoicing.errorApixListSupport">
                  <a href="mailto:servicedesk@apixmessaging.com" />
                </Trans>
              </li>
            </StyledErrorList>
          </>
        }
      />

      <Alert
        title={t('settings.einvoicing.activate')}
        description={
          <OnboardContent>
            <p>{t('settings.einvoicing.activateDescription1')}</p>
            <p>{t('settings.einvoicing.activateDescription2')}</p>

            <StyledCheckbox>
              <Checkbox
                id="grant-einvoicing-access"
                value={tosAccepted}
                onChange={e => setTosAccepted(e.target.checked)}
                disabled={isLoadingPaymentMethod}
                label={
                  <Trans i18nKey="settings.einvoicing.tosCheckbox">
                    <a href={t('link.pricing')} target="_blank" rel="noreferrer" />
                    <a href={t('link.apixTos')} target="_blank" rel="noreferrer" />
                  </Trans>
                }
              />
            </StyledCheckbox>

            <StyledButtonWrapper>
              <Button
                intent="primary"
                icon={<FaCheck />}
                onClick={handleActivate}
                disabled={isLoading || !tosAccepted || isLoadingPaymentMethod}
                showSpinner={isLoading}
              >
                {t('settings.einvoicing.activateButton')}
              </Button>
            </StyledButtonWrapper>
          </OnboardContent>
        }
      />

      <BillingSetupModal
        isVisible={billingModalOpen}
        onClose={() => setBillingModalOpen(false)}
        mode="einvoicing"
        initialSelectedCardId={billingModalInitialCard}
        onStripeAction={() => void startPaymentMethodSetup({})}
        isStripeActionPending={isStartingPaymentMethodSetup}
        onManualSubmitSuccess={handleManualSubmitSuccess}
      />
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  margin: auto;
  border-radius: ${({ theme }) => theme.borderRadius.lg}rem;
  background: ${({ theme }) => theme.colors.neutralWhite};
`

const OnboardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  font-size: ${({ theme }) => theme.fontSize.md}rem;

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.neutralBlack};
  }
`

const StyledButtonWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm}rem;
`

const StyledErrorText = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.xs}rem 0;
`

const StyledErrorList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing.md}rem;
  display: grid;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const StyledCheckbox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};

  input {
    margin-right: ${({ theme }) => theme.spacing.xs}rem;
    width: 1.6rem;
    height: 1.6rem;
  }
`
